/**
 * Angle Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de ângulo.
 *
 * Base: degree (grau) - mais comum em aplicações práticas.
 * Nota: Embora radiano seja a unidade SI, grau é mais intuitivo para a maioria dos casos.
 *
 * Fontes:
 * - SI Brochure, 9th Edition (2019)
 * - NIST SP 811 (2008)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type AngleUnit =
  | "turn"
  | "degree"
  | "arcminute"
  | "arcsecond"
  | "milliarcsecond"
  | "radian"
  | "milliradian"
  | "gradian";

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fator exato: 180/π graus por radiano
 * Valor: 57.29577951308232...
 */
const DEGREES_PER_RADIAN = 180 / Math.PI;

/**
 * Fator exato: 180/(1000π) graus por milirradiano
 */
const DEGREES_PER_MILLIRADIAN = 180 / (1000 * Math.PI);

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de ângulo.
 *
 * Fatores EXATOS:
 * - turn: 360 (definição)
 * - degree: 1 (base)
 * - arcminute: 1/60 (definição)
 * - arcsecond: 1/3600 (definição)
 * - milliarcsecond: 1/3600000 (definição)
 * - gradian: 360/400 = 0.9 (definição)
 * - radian: 180/π (exato por definição matemática)
 * - milliradian: 180/(1000π) (derivado)
 */
export const ANGLE_UNITS: Record<AngleUnit, SimpleUnitConfig> = {
  // Volta completa
  turn: {
    factor: 360,
    symbol: "tr",
    singular: "turn",
    plural: "turns",
  },

  // Base
  degree: {
    factor: 1,
    symbol: "°",
    singular: "degree",
    plural: "degrees",
    noSpace: true, // 45° não 45 °
  },

  // Subdivisões do grau
  arcminute: {
    factor: 1 / 60, // 0.016666...
    symbol: "′",
    singular: "arcminute",
    plural: "arcminutes",
    noSpace: true, // 30′ não 30 ′
  },
  arcsecond: {
    factor: 1 / 3600, // 0.000277...
    symbol: "″",
    singular: "arcsecond",
    plural: "arcseconds",
    noSpace: true, // 45″ não 45 ″
  },
  milliarcsecond: {
    factor: 1 / 3600000, // 2.777...e-7
    symbol: "mas",
    singular: "milliarcsecond",
    plural: "milliarcseconds",
  },

  // Radianos (unidade SI)
  radian: {
    factor: DEGREES_PER_RADIAN, // 180/π ≈ 57.2958
    symbol: "rad",
    singular: "radian",
    plural: "radians",
  },
  milliradian: {
    factor: DEGREES_PER_MILLIRADIAN, // 180/(1000π) ≈ 0.0573
    symbol: "mrad",
    singular: "milliradian",
    plural: "milliradians",
  },

  // Gradiano (gon)
  gradian: {
    factor: 0.9, // 360/400
    symbol: "gon",
    singular: "gradian",
    plural: "gradians",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const ANGLE_ALIASES: UnitAliases = {
  // turn
  tr: "turn",
  rev: "turn",
  // degree
  "°": "degree",
  deg: "degree",
  // arcminute
  "′": "arcminute",
  "'": "arcminute",
  arcmin: "arcminute",
  // arcsecond
  "″": "arcsecond",
  '"': "arcsecond",
  arcsec: "arcsecond",
  // milliarcsecond
  mas: "milliarcsecond",
  // radian
  rad: "radian",
  // milliradian
  mrad: "milliradian",
  // gradian
  gon: "gradian",
  grad: "gradian",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const ANGLE_UNIT_LIST = Object.keys(ANGLE_UNITS) as AngleUnit[];

/** Fatores de conversão (para compatibilidade) */
export const ANGLE_FACTORS: Record<AngleUnit, number> = Object.fromEntries(
  Object.entries(ANGLE_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<AngleUnit, number>;
